import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Grid } from '@material-ui/core';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { currentCountry, useGlobal } from '@rcg/ui-react';
import Button from '../../components/UiComponents/Button';
import Dictionary from '../../utils/dictionary';
import useDictionary from '../../hooks/useDictionary';
import useStyles from './index.style';
import useStylesProgressLine from '../ReviewFlight/index.style';
import ReviewCard from '../../components/ReviewCard';
import ProgressLineItems from '../../components/ProgressLine';
import {
  browseParamsVar,
  selectedDepartureVar,
  selectedReturnVar,
  prefixPathVar,
  searchResultsVar,
} from '../../operations/localState/reactiveVariables';
import { GET_CRUISE, SEARCH_PACKAGE_BY_CRUISE } from '../../operations/queries';
import { trackSummaryView } from '../../utils/analytics/browseTracking';
import {
  BRAND,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  FLIGHT_TYPE_CODES,
  ENABLE_QUICK_BOOK,
  CEL_BOOK_PARAMS,
  ROYAL_BOOK_PARAMS,
  CEL_CRUISE_TYPES,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  SESSION_STORAGE_AKAMAI_COUNTRY,
} from '../../utils/constants';
import { getSessionStorageItem } from '../../operations/sessionStorage/utils';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';
import { trackInteraction } from '../../utils/analytics/bookFlightsTracking';
import getBrand from '../../utils/getBrand';
import useLdFlags from '../../hooks/useLdFlags';
import AirLoader from '../../components/AirLoader';
import ItineraryDetails from '../../components/ItineraryDetails';

const CEL_LEGACY_BOOK_PATH = `booking-cruise/selectRoom/stateroomQuantity`;
const CEL_CRUISES_PATH = `cruises`;
const ROYAL_LEGACY_BOOK_PATH = `booking/landing`;
const QUICK_BOOK_PATH = `room-selection/review`;

const BrowseSummary = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const classesProgressLine = useStylesProgressLine();
  const [showLoader, setShowLoader] = useState(true);
  const [packageCode, setPackageCode] = useState(null);
  const [itinerary, setItinerary] = useState(null);
  const [cruiseType, setCruiseType] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [expanded, setExpanded] = useState(window.screen.width > 767);
  const getDictionaryValue = useDictionary(Dictionary);
  const { ldFlags, loading } = useLdFlags();
  const { data: globalData } = useGlobal();

  const selectedDepartureFlight = useReactiveVar(selectedDepartureVar);
  const selectedReturnFlight = useReactiveVar(selectedReturnVar);
  const searchResults = useReactiveVar(searchResultsVar);
  const browseParams = useReactiveVar(browseParamsVar);

  const [searchPackageCode] = useLazyQuery(SEARCH_PACKAGE_BY_CRUISE);
  const [getCruise] = useLazyQuery(GET_CRUISE, {
    fetchPolicy: 'cache-first',
    context: {
      headers: {
        office: globalData?.office?.toUpperCase(),
        currency: globalData?.currency?.toUpperCase(),
        country: globalData?.country?.toUpperCase(),
        language: globalData?.lang?.toUpperCase(),
      },
    },
  });

  const extraData = {
    departureFlight: selectedDepartureFlight || selectedReturnFlight,
    returnFlight: selectedReturnFlight || selectedDepartureFlight,
  };
  const data = {
    outboundFlight: selectedDepartureFlight?.flight || selectedReturnFlight?.flight,
    inboundFlight: selectedReturnFlight?.flight || selectedDepartureFlight?.flight,
  };
  const flightTypeCodeFromSession = getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE);
  const departureFlightResults = getSessionStorageItem(
    SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  );
  const isOneWay = flightTypeCodeFromSession === FLIGHT_TYPE_CODES.ONE_WAY;
  const lastSearchResults = searchResults || departureFlightResults;
  const isCelebrity = getBrand()?.id === BRAND.CELEBRITY;
  const isQuickBookEnabled = ldFlags?.[ENABLE_QUICK_BOOK];

  const loadAsyncData = async () => {
    setShowLoader(true);
    try {
      const shipCode = browseParams.ship.code;
      const sailDate = lastSearchResults?.shipBrowseFlight?.sailing?.sailDate;

      const packageCodeResp = await searchPackageCode({ variables: { sailDate, shipCode } });
      const packageCodeInfo = packageCodeResp.data?.searchPackageCodeByCruiseInfo;
      setPackageCode(packageCodeInfo);

      const cruiseResp = await getCruise({
        variables: { cruiseId: `${packageCodeInfo}_${sailDate}` },
      });
      const cruise = cruiseResp.data?.cruise;
      const groupIdInfo = cruise?.id;
      setGroupId(groupIdInfo);

      const firstSailing = cruise?.sailings?.[0];
      const itineraryInfo = firstSailing?.itinerary;
      const cruiseTypeInfo = itineraryInfo?.type || '';
      setCruiseType(cruiseTypeInfo);

      if (itineraryInfo) {
        setItinerary({
          ...(itineraryInfo || {}),
          ship: {
            name: browseParams?.ship?.label,
          },
        });
      }
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    trackSummaryView();
    loadAsyncData();
  }, []);

  const getDynamicPath = () => {
    if (isCelebrity) {
      return isQuickBookEnabled ? CEL_CRUISES_PATH : CEL_LEGACY_BOOK_PATH;
    }
    return isQuickBookEnabled ? QUICK_BOOK_PATH : ROYAL_LEGACY_BOOK_PATH;
  };

  const getParamsFromService = () => {
    const shipCode = browseParams.ship.code;
    const sailDate = lastSearchResults?.shipBrowseFlight?.sailing?.sailDate;
    const destinationCode = browseParams?.searchCriteria?.departureAirportCode;
    const currency = extraData.departureFlight?.cabin?.currency;
    const country = currentCountry().toUpperCase();
    const cruiseTypeCode = CEL_CRUISE_TYPES[cruiseType] || CEL_CRUISE_TYPES.CRUISE;
    const isoCode = globalData?.isoCode?.toLowerCase() || 'us';
    const language = globalData?.lang;

    return {
      country,
      isoCode,
      currency,
      destinationCode,
      groupId,
      packageCode,
      shipCode,
      sailDate,
      cruiseTypeCode,
      language,
    };
  };

  const getPrefixPath = ({ country, language, isoCode }) => {
    if (isCelebrity) {
      if (
        (country.toUpperCase() === 'CAN' || country.toUpperCase() === 'USA') &&
        language.toLowerCase() === 'fr'
      ) {
        return `/ca/fr`;
      }
      return isQuickBookEnabled ? `/${isoCode.toLowerCase()}/${language.toLowerCase()}` : '';
    }
    return prefixPathVar();
  };

  const getUrlParams = (params) => {
    const urlParams = new URLSearchParams();
    if (isCelebrity) {
      if (!isQuickBookEnabled) {
        urlParams.append(CEL_BOOK_PARAMS.COUNTRY, params.country);
        urlParams.append(CEL_BOOK_PARAMS.GROUP_ID, params.groupId);
        urlParams.append(CEL_BOOK_PARAMS.PACKAGE_CODE, params.packageCode);
        urlParams.append(CEL_BOOK_PARAMS.SAIL_DATE, params.sailDate);
        urlParams.append(CEL_BOOK_PARAMS.SHIP_CODE, params.shipCode);
        urlParams.append(CEL_BOOK_PARAMS.CRUISE_TYPE, params.cruiseTypeCode);
      }
    } else {
      urlParams.append(ROYAL_BOOK_PARAMS.GROUP_ID, params.groupId);
      urlParams.append(ROYAL_BOOK_PARAMS.PACKAGE_CODE, params.packageCode);
      urlParams.append(ROYAL_BOOK_PARAMS.SAIL_DATE, params.sailDate);
      urlParams.append(ROYAL_BOOK_PARAMS.SHIP_CODE, params.shipCode);
      urlParams.append(ROYAL_BOOK_PARAMS.DESTINATION_CODE, params.destinationCode);
      urlParams.append(ROYAL_BOOK_PARAMS.CURRENCY_CODE, params.currency);
      urlParams.append(ROYAL_BOOK_PARAMS.COUNTRY, params.country);
    }
    return urlParams;
  };

  const setWucAkamaiCountry = ({ country }) => {
    if (isCelebrity) {
      sessionStorage.setItem(
        SESSION_STORAGE_AKAMAI_COUNTRY,
        JSON.stringify({ country: country.toUpperCase() }),
      );
    }
  };

  const navigateToBook = () => {
    setShowLoader(true);
    let redirectUrl = '';
    let prefixPath = '';
    if (!groupId) {
      prefixPath = prefixPathVar();
      redirectUrl = `${prefixPath}${getDictionaryValue(Dictionary.airRoyalHomeLink.key)}`;
    } else {
      const params = getParamsFromService();
      const path = getDynamicPath();
      const urlParams = getUrlParams(params);
      setWucAkamaiCountry(params);
      prefixPath = getPrefixPath(params);
      redirectUrl = `${prefixPath}/${path}?${urlParams.toString()}`;
    }
    window.location.assign(redirectUrl);
  };

  const bookCruise = async () => {
    trackInteraction({ interaction: `air:bookCruise:clicked` });
    navigateToBook();
  };

  const guests = Array.from({ length: browseParams?.searchCriteria?.numberOfGuests }, () => ({
    flightPassengerType: 'ADULT',
  }));

  const sailing = {
    itinerary,
    sailDate: lastSearchResults?.shipBrowseFlight?.sailing?.sailDate,
    sailTime: lastSearchResults?.shipBrowseFlight?.sailing?.sailTime,
  };

  return (
    <>
      <Helmet>
        <title>{getDictionaryValue(Dictionary.airBrowseReviewFlightSeoTitle.key)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      {itinerary && <ItineraryDetails sailing={sailing} flightProfile={{ guests }} />}
      <Grid container className={classes.gridContainer}>
        {(showLoader || loading) && <AirLoader open data-testid="loader" />}
        <Grid item xs={12} className={classes.browseSummaryContainer}>
          <div className={classesProgressLine.progressLineContainer}>
            <ProgressLineItems selectedFlightType={flightTypeCodeFromSession} />
          </div>
          <div className={classes.browseSummaryCardWrapper}>
            <div
              className={`${commonClasses.notification} ${classes.notification}`}
              data-testid="notification-banner">
              <NotificationBannerComponent
                title={getDictionaryValue(Dictionary.browseSummaryBannerTitle.key)}
                type="warning"
                renderCloseBtn
                useSlot>
                <div className={classes.notificationSlot}>
                  <div className={classes.notificationDesc}>
                    {' '}
                    {getDictionaryValue(Dictionary.browseSummaryBannerDescription.key)}
                  </div>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={bookCruise}
                    data-testid="quickbook-cta">
                    {getDictionaryValue(Dictionary.browseSummaryBannerCTA.key)}
                  </Button>
                </div>
              </NotificationBannerComponent>
            </div>
            <ReviewCard
              title={getDictionaryValue(Dictionary.airBrowseSummaryTitle.key)}
              subTitle={getDictionaryValue(Dictionary.airBrowseSummaryDescription.key)}
              data={data}
              extraData={extraData}
              isSummary
              handleAccordion={() => setExpanded(!expanded)}
              isDesktop={expanded}
              cardTitle={getDictionaryValue(Dictionary.airFlightSummaryTitle.key)}
              summaryBalance
              isOneWay={isOneWay}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default BrowseSummary;
